import React, { useEffect, useState } from "react";
import { message } from "antd";
import {
  CreditCardOutlined,
  EditOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  LockOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./Subscription.css";
import { canManageBilling, getCurrentRole } from "../../utils/roles";
import {
  downloadAllInvoices,
  downloadInvoice,
  getInvoices,
  getPlans,
  getSeatUsage,
  getSubscription,
  getTokenBalance,
  getCurrentPlan,
  reactivateSubscription,
  triggerBrowserDownload,
} from "./subscription.service";
import {
  BillingCycle,
  Invoice,
  PaymentMethod,
  Plan,
  PlanChangeResult,
  SeatUsage,
  Subscription,
  TokenBalance,
} from "./subscription.types";
import PlanCard from "./components/PlanCard";
import BillingHistoryTable from "./components/BillingHistoryTable";
import CancelSubscriptionModal from "./components/CancelSubscriptionModal";
import EnterpriseContactModal from "./components/EnterpriseContactModal";
import PaymentMethodModal from "./components/PaymentMethodModal";
import ChangePlanModal from "./components/ChangePlanModal";
import TokensSection from "./components/TokensSection";
import BuyTokensModal from "./components/BuyTokensModal";
import PlanComparisonModal from "./components/PlanComparisonModal";
import { useGate } from "./entitlements";

const PAGE_SIZE = 10;

const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation("subscription");
  const gate = useGate();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>("yearly");
  const [loading, setLoading] = useState(true);

  const [tokenBalance, setTokenBalance] = useState<TokenBalance | null>(null);
  const [seatUsage, setSeatUsage] = useState<SeatUsage | null>(null);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [changeTarget, setChangeTarget] = useState<Plan | null>(null);
  const [buyTokensOpen, setBuyTokensOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [planList, sub, balance, seats, current] = await Promise.all([
          getPlans(),
          getSubscription(),
          getTokenBalance(),
          getSeatUsage(),
          getCurrentPlan(),
        ]);
        setPlans(planList);
        setSubscription(sub);
        setCurrentPlan(current ?? null);
        setCycle(sub.billingCycle);
        setTokenBalance(balance);
        setSeatUsage(seats);
      } catch {
        message.error(t("subscriptionPage.loadError"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Invoices (paginated) ────────────────────────────────────────────────────
  useEffect(() => {
    setInvoiceLoading(true);
    getInvoices(invoicePage, PAGE_SIZE)
      .then(({ invoices: rows, total }) => {
        setInvoices(rows);
        setInvoiceTotal(total);
      })
      .finally(() => setInvoiceLoading(false));
  }, [invoicePage]);

  const refreshSubscription = async () => {
    const sub = await getSubscription();
    setSubscription(sub);
  };

  // Re-fetch billing history after anything that can produce a new invoice
  // (a plan change with an immediate charge, a credits top-up). Jumps back to
  // page 1 so a fresh row is visible without a manual reload.
  const refreshInvoices = async () => {
    if (invoicePage !== 1) {
      setInvoicePage(1);
      return; // the [invoicePage] effect above will refetch page 1
    }
    setInvoiceLoading(true);
    try {
      const { invoices: rows, total } = await getInvoices(1, PAGE_SIZE);
      setInvoices(rows);
      setInvoiceTotal(total);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const cancelled = subscription?.status === "cancelled";
  const onPaidPlan = !!currentPlan && !currentPlan.isFree;

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChoose = (plan: Plan) => {
    if (plan.customPricing) {
      setContactOpen(true);
      return;
    }
    // The backend has no "change to Free" — Free means no subscription, so
    // the way down is cancelling (access continues until the period ends).
    if (plan.isFree && onPaidPlan) {
      if (cancelled) {
        message.info(
          t("subscriptionPage.downgradeScheduled", {
            date:
              subscription?.accessUntil ??
              t("subscriptionPage.currentPeriodEndsFallback"),
          }),
        );
        return;
      }
      setCancelOpen(true);
      return;
    }
    setChangeTarget(plan);
  };

  const handleReactivate = async (plan: Plan) => {
    if (plan.customPricing) {
      setContactOpen(true);
      return;
    }
    if (reactivating) return; // guard against a double-click firing two requests
    setReactivating(true);
    try {
      await reactivateSubscription(plan.id, cycle);
      await refreshSubscription();
      gate.refresh();
      message.success(
        t("subscriptionPage.reactivateSuccess", { planName: plan.name }),
      );
    } catch (e: any) {
      message.error(
        e?.response?.data?.detail || t("subscriptionPage.reactivateError"),
      );
    } finally {
      setReactivating(false);
    }
  };

  const handleDetails = (_plan: Plan) => {
    setCompareOpen(true);
  };

  const handleCancelled = (accessUntil: string) => {
    setSubscription((prev) =>
      prev ? { ...prev, status: "cancelled", accessUntil } : prev,
    );
  };

  const handlePaymentSaved = () => {
    // The card is saved on Stripe's side; refetch to show the real brand/last4.
    setPaymentOpen(false);
    refreshSubscription();
  };

  const handlePlanChanged = (_result: PlanChangeResult) => {
    // Reflect the new current plan behind the success screen, and sync the
    // app-wide gate so every other module (Dashboard, Reporting, etc.) sees
    // the new plan immediately instead of only after a full page reload.
    refreshSubscription();
    refreshInvoices();
    gate.refresh();
  };

  const handleTokensBought = (balance: TokenBalance) => {
    setTokenBalance(balance);
    setBuyTokensOpen(false);
    message.success(t("subscriptionPage.tokensAdded"));
    refreshInvoices();
    gate.refresh();
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      const { blob, filename } = await downloadInvoice(invoice.id);
      triggerBrowserDownload(blob, filename);
    } catch {
      message.error(t("subscriptionPage.downloadInvoiceError"));
    }
  };

  const handleDownloadAllInvoices = async () => {
    try {
      const { blob, filename } = await downloadAllInvoices();
      triggerBrowserDownload(blob, filename);
    } catch {
      message.error(t("subscriptionPage.downloadAllError"));
    }
  };

  // Billing is Owner-only — everyone else sees a friendly notice.
  const isOwner = canManageBilling(getCurrentRole());
  if (!isOwner) {
    return (
      <div className="subscription-page">
        <div className="subscription-card">
          <div className="sub-header">
            <h1 className="sub-title">{t("subscriptionPage.title")}</h1>
          </div>
          <div className="sub-owner-only">
            <LockOutlined />
            <p>{t("subscriptionPage.ownerOnlyNotice")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !subscription) {
    return (
      <div className="subscription-page">
        <div className="subscription-card">{t("subscriptionPage.loading")}</div>
      </div>
    );
  }

  const pm = subscription.paymentMethod;

  return (
    <div className="subscription-page">
      <div className="subscription-card">
        {/* Header */}
        <div className="sub-header">
          <h1 className="sub-title">{t("subscriptionPage.title")}</h1>
          {cancelled ? (
            <span className="sub-cancelled-flag">
              <CloseCircleOutlined /> {t("subscriptionPage.cancelledFlag")}
            </span>
          ) : (
            onPaidPlan && (
              <button
                className="sub-cancel-btn"
                onClick={() => setCancelOpen(true)}
              >
                {t("subscriptionPage.cancelSubscriptionButton")}
              </button>
            )
          )}
        </div>

        {cancelled && (
          <div className="sub-cancel-banner">
            <WarningOutlined style={{ marginTop: 2 }} />
            <span>
              {t("subscriptionPage.cancelBanner.before")}{" "}
              <strong>{subscription.accessUntil}</strong>
              {t("subscriptionPage.cancelBanner.after")}
            </span>
          </div>
        )}

        {/* Current subscription + plans */}
        <div className="sub-section-head">
          <h2 className="sub-section-title">
            {t("subscriptionPage.currentSubscription")}
          </h2>
          <div className="sub-cycle-toggle">
            <button
              className={cycle === "yearly" ? "active" : ""}
              onClick={() => setCycle("yearly")}
            >
              {t("subscriptionPage.billedYearly")}
            </button>
            <button
              className={cycle === "monthly" ? "active" : ""}
              onClick={() => setCycle("monthly")}
            >
              {t("subscriptionPage.billedMonthly")}
            </button>
          </div>
        </div>

        {seatUsage && (
          <div className="sub-seat-usage">
            {t("subscriptionPage.seatUsage.workingUsers")}{" "}
            <strong>
              {seatUsage.workingUsersUsed}
              {seatUsage.workingUserLimit != null
                ? ` / ${seatUsage.workingUserLimit}`
                : ""}
            </strong>{" "}
            &middot; {t("subscriptionPage.seatUsage.contributorsViewers")}{" "}
            <strong>{t("subscriptionPage.seatUsage.unlimitedFree")}</strong>
          </div>
        )}

        {currentPlan?.unlimitedCredits && currentPlan?.workingUserLimit === null && (
          <div className="sub-plan-banner">
            <InfoCircleOutlined />
            <span>
              Your organization is using <strong>i-APS Internal Unlimited</strong> — everything is unlocked.
            </span>
          </div>
        )}

        <div className="sub-plans">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              cycle={cycle}
              isCurrent={subscription.currentPlanId === plan.id}
              cancelled={cancelled}
              accessUntil={subscription.accessUntil}
              reactivating={reactivating}
              onChoose={handleChoose}
              onContact={() => setContactOpen(true)}
              onReactivate={handleReactivate}
              onDetails={handleDetails}
            />
          ))}
        </div>

        {/* Next payment + payment method */}
        <div className="sub-info-row">
          <div className="sub-info-box">
            <div className="sub-info-label">
              {t("subscriptionPage.nextPaymentDue")}
            </div>
            <div className="sub-info-due">
              {cancelled || !onPaidPlan
                ? t("subscriptionPage.noFurtherCharges")
                : subscription.nextPaymentDue}
              {!cancelled && onPaidPlan && subscription.nextInvoiceAmount != null && (
                <> &middot; ${subscription.nextInvoiceAmount.toFixed(2)}</>
              )}
            </div>
          </div>
          <div className="sub-info-box sub-pm-box">
            <div className="sub-pm-main">
              <span className="sub-pm-icon">
                <CreditCardOutlined />
              </span>
              <div>
                <div className="sub-pm-kind">{t("common.creditCard")}</div>
                <div className="sub-pm-detail">
                  {pm
                    ? t("common.cardEndingIn", {
                        brand: pm.brand,
                        last4: pm.last4,
                      })
                    : t("common.noCardOnFile")}
                </div>
              </div>
            </div>
            <button className="sub-edit-btn" onClick={() => setPaymentOpen(true)}>
              <EditOutlined /> {t("common.edit")}
            </button>
          </div>
        </div>

        {/* Translation tokens — hidden for plans with unlimited credits */}
        {tokenBalance && !currentPlan?.unlimitedCredits && (
          <TokensSection
            balance={tokenBalance}
            onBuy={() => setBuyTokensOpen(true)}
            unlimitedCredits={currentPlan?.unlimitedCredits}
          />
        )}

        {/* Billing history */}
        <BillingHistoryTable
          invoices={invoices}
          loading={invoiceLoading}
          page={invoicePage}
          pageSize={PAGE_SIZE}
          total={invoiceTotal}
          onPageChange={setInvoicePage}
          onDownloadAll={handleDownloadAllInvoices}
          onDownloadOne={handleDownloadInvoice}
        />
      </div>

      {/* Modals */}
      <CancelSubscriptionModal
        open={cancelOpen}
        accessUntil={subscription.accessUntil ?? subscription.nextPaymentDue ?? ""}
        onClose={() => setCancelOpen(false)}
        onCancelled={handleCancelled}
      />
      <EnterpriseContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
      <PaymentMethodModal
        open={paymentOpen}
        method={subscription.paymentMethod}
        onClose={() => setPaymentOpen(false)}
        onSaved={handlePaymentSaved}
      />
      <ChangePlanModal
        open={!!changeTarget}
        targetPlan={changeTarget}
        cycle={cycle}
        paymentMethod={subscription.paymentMethod}
        onClose={() => setChangeTarget(null)}
        onChanged={handlePlanChanged}
        onEditPayment={() => setPaymentOpen(true)}
      />
      <BuyTokensModal
        open={buyTokensOpen}
        onClose={() => setBuyTokensOpen(false)}
        onBought={handleTokensBought}
      />
      <PlanComparisonModal
        open={compareOpen}
        plans={plans}
        cycle={cycle}
        currentPlanId={subscription.currentPlanId}
        onClose={() => setCompareOpen(false)}
      />
    </div>
  );
};

export default SubscriptionPage;
